import apis from '../../utils/apis';
import { readErrorMessage } from '../../utils/errorMessage';

/**
 * Two endpoints, one role.
 *
 * Roles come from two tables and therefore two routes. Custom roles an org
 * created live at `/admin/roles/:id`. The built-in catalogue — ids prefixed
 * `static_`, served as `org_type: "universal"` — lives at
 * `/role-permissions/:id`, and asking the custom endpoint for one gets you a
 * 400 with `"Role static_19 is not a custom role. Use /role-permissions/…"`.
 *
 * The detail view used to call only the custom route, so every built-in role
 * hung on "Loading…" behind that error. Everything about the split is settled
 * here — the view takes one `RoleDetail` and never learns which route it came
 * from.
 */

/** The shape the view renders, whichever endpoint answered. */
export interface RoleDetail {
  id: string | number;
  /** Machine name (`managing_partner`). */
  name: string;
  /**
   * Human name, ONLY when the backend actually supplies one. Left undefined
   * otherwise so the view can fall back to `formatRoleNameForDisplay(name)` —
   * defaulting it to the raw machine name here would silently defeat that
   * formatter and print `managing_partner` on the page.
   */
  display_name?: string;
  description?: string;
  /** Numeric ids, present on custom roles. */
  permission_ids: number[];
  /** Permission strings (`matters:read`), how the static catalogue reports. */
  permission_names: string[];
  /** False for the built-in catalogue — the view renders those read-only. */
  is_custom: boolean;
}

export const CUSTOM_ROLE_URL = (id: string) => `/admin/roles/${encodeURIComponent(id)}`;
export const STATIC_ROLE_URL = (id: string) => `/role-permissions/${encodeURIComponent(id)}`;

/** Built-in roles are the `static_`-prefixed ids. */
export const isStaticRoleId = (id: string | number | undefined | null): boolean =>
  /^static_/i.test(String(id ?? ''));

/**
 * The backend's own words for "you asked the wrong endpoint".
 *
 * Belt and suspenders: the id prefix is the primary signal, and this catches a
 * built-in role that arrives without one.
 */
export const isNotCustomRoleError = (err: any): boolean => {
  const message = String(
    readErrorMessage(err, '')
  );
  return /not a custom role|role-permissions/i.test(message);
};

/** `permissions` comes back as an array, or as a JSON array in a string. */
const toStringArray = (value: any): string[] => {
  if (Array.isArray(value)) return value.map(String);
  if (typeof value === 'string' && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch {
      return [];
    }
  }
  return [];
};

const toNumberArray = (value: any): number[] =>
  Array.isArray(value) ? value.map(Number).filter((n) => !Number.isNaN(n)) : [];

/**
 * One role model out of either payload.
 *
 * Static rows name themselves `role_name` and list `permissions` as strings;
 * custom rows use `name` and `permission_ids`. `isCustom` is passed in rather
 * than sniffed, because it is the endpoint that actually answered that decides
 * whether this role can be edited — a static row is read-only even if it
 * happens to carry an `is_custom` field.
 */
export const normalizeRole = (raw: any, isCustom: boolean): RoleDetail => {
  const name = String(raw?.name ?? raw?.role_name ?? '');
  return {
    id: raw?.id ?? raw?.role_id ?? '',
    name,
    display_name: raw?.display_name ? String(raw.display_name) : undefined,
    description: raw?.description ?? undefined,
    permission_ids: toNumberArray(raw?.permission_ids),
    permission_names: toStringArray(raw?.permission_names ?? raw?.permissions),
    // A role served by the static catalogue is never editable. On the custom
    // route, trust an explicit `is_custom: false` (a universal role reachable
    // by id) over the route itself.
    is_custom: isCustom && raw?.is_custom !== false && raw?.org_type !== 'universal',
  };
};

const unwrap = (res: any) => res?.data?.data ?? res?.data?.role ?? null;

/**
 * Fetch a role from whichever endpoint owns it.
 *
 * Branch on the id first, so the common case is one request. Either way the
 * other endpoint is the fallback: a `static_` id that the catalogue does not
 * know may be a custom role with an odd name, and a custom id that answers
 * "not a custom role" is a built-in one. The ORIGINAL error is what surfaces
 * if both fail — the fallback's 404 is noise.
 */
export const fetchRoleDetail = async (roleId: string): Promise<RoleDetail> => {
  const preferStatic = isStaticRoleId(roleId);
  const first = preferStatic ? STATIC_ROLE_URL(roleId) : CUSTOM_ROLE_URL(roleId);
  const second = preferStatic ? CUSTOM_ROLE_URL(roleId) : STATIC_ROLE_URL(roleId);

  let raw: any;
  let servedByCustomRoute = !preferStatic;

  try {
    const res = await apis.get(first);
    raw = unwrap(res);
    if (!raw) throw new Error('Role data is empty in response');
  } catch (firstErr: any) {
    try {
      const res = await apis.get(second);
      raw = unwrap(res);
      if (!raw) throw new Error('Role data is empty in response');
      servedByCustomRoute = preferStatic;
    } catch {
      throw firstErr;
    }
  }

  return normalizeRole(raw, servedByCustomRoute);
};

export interface RolePermissionCatalogue {
  id: number;
  name: string;
  category?: string;
  description?: string;
  permissions: { id: number; name: string; section_id: number; description?: string }[];
}

export const fetchPermissionCatalogue = async (): Promise<RolePermissionCatalogue[]> => {
  const res = await apis.get('/admin/permissions');
  return res?.data?.data || [];
};
